import { ArrowUpRight, GraduationCap, Award, Landmark, Linkedin } from "lucide-react";
import { CALENDLY_URL, LINKEDIN_URL, JAKE_LINKEDIN_URL } from "../lib/site";
import Seo from "../components/Seo";
import { businessSchema, personSchema, jakeSchema } from "../lib/seoSchemas";
import { useContent } from "../lib/content";

const credentials = [
  "Doctor of Business Administration (DBA), Organizational Leadership",
  "Educational Specialist (Ed.S.), Organizational Leadership",
  "M.S., Management and Leadership",
  "B.S., Public Administration",
  "ServSafe Certified Instructor and Registered Proctor",
];

const civicService = [
  "Commissioner, City of Independence Board of Adjustment",
  "Active board and civic leadership positions in the Kansas City area",
];

const milestones = [
  {
    label: "Interim Presidencies",
    body: "Interim President across 9 portfolio companies inside a 19-company PE-backed food and beverage portfolio.",
  },
  {
    label: "Portfolio Revenue Directed",
    body: "$119M+ in combined annual revenue and 1,785 employees directed across 13 states.",
  },
  {
    label: "Negative-EBITDA Reversals",
    body: "Reversed negative EBITDA at 4 companies within 6 months of engagement, stabilizing P&L, leadership, and operations from the ground up.",
  },
  {
    label: "Executive Leaders Placed",
    body: "6 executive leaders personally recruited, vetted, and transitioned into seat.",
  },
  {
    label: "Managers Developed",
    body: "200+ unit-level managers built and promoted through structured development pipelines.",
  },
  {
    label: "New Restaurant Builds",
    body: "20+ new restaurant openings directed from lease signing through opening day.",
  },
  {
    label: "Series A People Ops",
    body: "Built the entire people operations function at a Series A start-up, scaling from 55 to 120 employees in 12 months.",
  },
  {
    label: "Franchise Systems",
    body: "Established the first centralized training and recruitment department across a 51-location Domino's franchise network.",
  },
];

export default function About() {
  const { t } = useContent();
  return (
    <div data-testid="page-about">
      <Seo
        title={t("about.seo.title")}
        description={t("about.seo.description")}
        type="profile"
        image="https://holderhospitality.consulting/images/caleb.png"
        schemas={[businessSchema, personSchema, jakeSchema]}
      />
      {/* HERO */}
      <section className="bg-white py-20 lg:py-28 border-b border-ink-line" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="eyebrow mb-6">{t("about.hero.eyebrow")}</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-brand-700 leading-[1.02] tracking-tight">
              {t("about.hero.heading")}
            </h1>
            <p className="mt-6 text-lg text-ink-soft leading-relaxed">
              {t("about.hero.intro")}
            </p>
          </div>
        </div>
      </section>

      {/* CALEB PROFILE */}
      <section className="bg-white py-24" data-testid="about-caleb">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div
                className="aspect-[4/5] bg-white overflow-hidden border border-brand-200 lg:sticky lg:top-28"
                data-testid="about-headshot"
              >
                <img
                  src="/images/caleb.png"
                  alt="Caleb R. Holder, DBA, founder of Holder Hospitality Consulting and fractional COO for restaurant and hospitality operators"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-7 lg:pl-8">
              <div className="eyebrow mb-4">Leadership</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight">
                {t("about.caleb.name")}
              </h2>
              <p className="mt-2 text-sm tracking-[0.15em] uppercase text-brand-500">
                {t("about.caleb.title")}
              </p>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-brand-700 text-sm font-medium hover:text-brand-500 transition-colors"
                data-testid="about-caleb-linkedin"
              >
                <Linkedin className="w-4 h-4" /> LinkedIn
              </a>
              <p className="mt-8 text-lg text-ink-soft leading-relaxed">
                {t("about.caleb.bio1")}
              </p>
              <p className="mt-6 text-lg text-ink-soft leading-relaxed">
                {t("about.caleb.bio2")}
              </p>
              <p className="mt-6 text-lg text-ink-soft leading-relaxed">
                {t("about.caleb.bio3")}
              </p>
              <p className="mt-6 text-lg text-ink-soft leading-relaxed">
                {t("about.caleb.bio4")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* JAKE PROFILE */}
      <section className="bg-brand-100 py-24 border-t border-brand-200" data-testid="about-jake">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7 lg:pr-8 order-2 lg:order-1">
              <div className="eyebrow mb-4">Leadership</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight">
                {t("about.jake.name")}
              </h2>
              <p className="mt-2 text-sm tracking-[0.15em] uppercase text-brand-500">
                {t("about.jake.title")}
              </p>
              <a
                href={JAKE_LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-brand-700 text-sm font-medium hover:text-brand-500 transition-colors"
                data-testid="about-jake-linkedin"
              >
                <Linkedin className="w-4 h-4" /> LinkedIn
              </a>
              <p className="mt-8 text-lg text-ink-soft leading-relaxed">
                {t("about.jake.bio1")}
              </p>
              <p className="mt-6 text-lg text-ink-soft leading-relaxed">
                {t("about.jake.bio2")}
              </p>
              <p className="mt-6 text-lg text-ink-soft leading-relaxed">
                {t("about.jake.bio3")}
              </p>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2">
              <div
                className="aspect-[4/5] bg-white overflow-hidden border border-brand-200 lg:sticky lg:top-28"
                data-testid="about-jake-headshot"
              >
                <img
                  src="/images/jake.png"
                  alt="Jake Lair, co-founder and COO of Holder Hospitality Consulting"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-white py-24 lg:py-28 border-t border-ink-line" data-testid="about-philosophy">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="eyebrow mb-4">Our Philosophy</div>
              <h2 className="font-serif text-4xl text-brand-700 leading-tight">
                {t("about.philosophy.heading")}
              </h2>
            </div>
            <div className="lg:col-span-8 lg:pl-8 lg:border-l lg:border-ink-line">
              <p className="font-serif text-2xl sm:text-3xl text-brand-700 leading-snug">
                {t("about.philosophy.quote")}
              </p>
              <p className="mt-8 text-lg text-ink-soft leading-relaxed">
                {t("about.philosophy.body")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MILESTONES */}
      <section className="bg-white py-24 border-t border-ink-line" data-testid="about-milestones">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-4">Selected Engagements: Caleb R. Holder, DBA</div>
          <h2 className="font-serif text-4xl text-brand-700 leading-tight max-w-3xl mb-12">
            {t("about.milestones.heading")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-line border border-ink-line">
            {milestones.map((m, i) => (
              <div
                key={m.label}
                className="bg-white p-8 lg:p-10"
                data-testid={`milestone-${i}`}
              >
                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500 mb-3">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-serif text-2xl text-brand-700 mb-3">{m.label}</div>
                <p className="text-ink-soft leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EDUCATION + CIVIC */}
      <section className="bg-brand-100 py-24" data-testid="about-credentials">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-px bg-brand-200 border border-brand-200">
          <div className="bg-brand-100 p-10 lg:p-12">
            <div className="flex items-center gap-3 mb-6">
              <GraduationCap className="w-6 h-6 text-brand-700" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500">
                Education and Credentials: Caleb R. Holder
              </div>
            </div>
            <ul className="space-y-4">
              {credentials.map((c, i) => (
                <li
                  key={c}
                  className="flex items-start gap-3 text-ink-soft"
                  data-testid={`credential-${i}`}
                >
                  <Award className="w-4 h-4 text-brand-700 mt-1 shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-brand-100 p-10 lg:p-12">
            <div className="flex items-center gap-3 mb-6">
              <Landmark className="w-6 h-6 text-brand-700" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500">
                Civic and Board Service
              </div>
            </div>
            <ul className="space-y-4">
              {civicService.map((c, i) => (
                <li
                  key={c}
                  className="flex items-start gap-3 text-ink-soft"
                  data-testid={`civic-${i}`}
                >
                  <Award className="w-4 h-4 text-brand-700 mt-1 shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-700 text-white py-20" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight">
              {t("about.cta.heading")}
            </h2>
            <p className="mt-4 text-brand-100/90 max-w-xl">
              {t("about.cta.body")}
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-brand-700 hover:bg-brand-100 transition-colors px-7 py-4 text-sm font-medium"
              data-testid="about-cta-button"
            >
              {t("about.cta.button")} <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
